import { Box, Button, Group } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import React from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { useNavigate } from "react-router"
import useSWR from "swr"
import { OrderApi, SavedListApi } from "../../apis"
import { flattenCatalogue, useCatalogue } from "../../components/buyer-sales/product-list/catalogue-data"
import { toCartItem, useAddLinesToCart } from "../../components/buyer-sales/quick-order/cart-lines"
import qoClasses from "../../components/buyer-sales/quick-order/quick-order.module.css"
import { useBuyerSuppliers, useSupplierChoice } from "../../components/buyer-sales/quick-order/use-buyer-supplier"
import { useReorder } from "../../components/buyer-sales/reorder"
import { ReorderPanel } from "../../components/buyer-sales/saved-lists/ReorderPanel"
import classes from "../../components/buyer-sales/saved-lists/saved-lists.module.css"
import { SaveListModal } from "../../components/buyer-sales/saved-lists/SaveListModal"
import { SourceList, type SourceRow } from "../../components/buyer-sales/saved-lists/SourceList"
import { useReorderLines, type CheckedLine, type ReorderSource } from "../../components/buyer-sales/saved-lists/use-reorder-lines"
import { SupplierSelect } from "../../components/buyer-sales/supplier-select"
import { translateBackendError } from "../../components/delivery/translate-error"
import { TableFrame } from "../../components/delivery/atoms"
import { PageLayout } from "../../components/layout"
import { usePermission } from "../../components/permission"
import { DeniedCard, ListState, SegmentedTabs } from "../../components/ui"
import { Dialog } from "../../utils/confirm-modal"
import { dateFormat } from "../../utils/date"
import { message } from "../../utils/message"

type Tab = "saved" | "order"

/** Past orders shown on «Өмнө авсан»: the newest few, cancelled ones dropped. */
const RECENT_ORDERS = 10
const CANCELLED = ["CANCELED", "AUTO_CANCELED", "CANCELLED"]

/** «Хадгалсан жагсаалт» (B25, canvas OrdB_SavedLists): saved lists and past orders, re-ordered at today's price. */
export function BuyerSalesSavedLists() {
  const { t } = useTranslation(["bulkOrder", "order", "common", "supplierSales"])
  const navigate = useNavigate()
  const ORD_LIST = usePermission("ORD_LIST")
  const { suppliers, isLoading: suppliersLoading } = useBuyerSuppliers(ORD_LIST.hasAccess)
  const { supplierId, supplier, setSupplierId } = useSupplierChoice(suppliers)

  const [tab, setTab] = React.useState<Tab>("saved")
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  // Lines the buyer unticked, for one source; another source starts with everything ticked.
  const [unchecked, setUnchecked] = React.useState<{ source: string; ids: string[] }>({ source: "", ids: [] })
  const [saveOpened, { open: openSave, close: closeSave }] = useDisclosure(false)

  const saved = useSWR(
    supplierId && tab === "saved" ? ["saved-lists", supplierId] : null,
    () => SavedListApi.list(supplierId),
    { revalidateOnFocus: false },
  )
  const orders = useSWR(
    supplierId && tab === "order" ? ["saved-lists-orders", supplierId] : null,
    () =>
      OrderApi.list({
        filter: { type: "", orderStatus: "", deliveryNoteStatus: "", businessId: supplierId, startDate: "", endDate: "", invoiceStatus: "", paymentStatus: "", paymentTermCondition: "", excel: "false" },
        offset: { limit: RECENT_ORDERS, page: 1 },
      }),
    { revalidateOnFocus: false },
  )

  const rows: SourceRow[] = React.useMemo(() => {
    if (tab === "saved") {
      return (saved.data?.rows || []).map((r) => ({
        id: r.id,
        name: r.name,
        sub: t("bulkOrder:saved.lineCount", { count: r.lineCount || 0 }),
        date: dateFormat(r.updatedAt),
      }))
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((orders.data?.rows || []) as any[])
      .filter((o) => !CANCELLED.includes(o.orderStatus))
      .map((o) => {
        const base = o.salesCode ? t("bulkOrder:saved.yourPo", { code: o.purchaseCode || "-" }) : t("bulkOrder:saved.noSo")
        const count = Array.isArray(o.lines) ? `, ${t("bulkOrder:saved.lineCount", { count: o.lines.length })}` : ""
        return { id: o.id, name: o.salesCode || o.purchaseCode || "-", sub: `${base}${count}`, date: dateFormat(o.createdAt) }
      })
  }, [tab, saved.data, orders.data, t])

  const active = tab === "saved" ? saved : orders
  const listState = active.isLoading ? "loading" : active.error ? "error" : rows.length === 0 ? "empty" : "data"
  const current = rows.find((r) => r.id === selectedId) || rows[0] || null
  const source: ReorderSource | null = current ? { kind: tab, id: current.id, name: current.name, date: current.date } : null
  const sourceKey = source ? `${source.kind}:${source.id}` : ""

  const { lines, supplier: lineSupplier, isLoading: linesLoading, error: linesError, retry } = useReorderLines(source, supplier)
  const uncheckedIds = unchecked.source === sourceKey ? unchecked.ids : []
  const isSelected = (l: CheckedLine) => l.selectable && !uncheckedIds.includes(l.variantId)
  const selectedLines = lines.filter(isSelected)

  const catalogueSuppliers = React.useMemo(() => (supplier ? [supplier] : []), [supplier])
  const { data: catalogue } = useCatalogue(catalogueSuppliers)
  const addLines = useAddLinesToCart()
  const { reorderById } = useReorder()

  const handleToggle = (l: CheckedLine) =>
    setUnchecked({
      source: sourceKey,
      ids: uncheckedIds.includes(l.variantId) ? uncheckedIds.filter((id) => id !== l.variantId) : [...uncheckedIds, l.variantId],
    })

  const handleTab = (next: Tab) => {
    setTab(next)
    setSelectedId(null)
  }

  const handleAddPanel = () =>
    addLines(
      selectedLines.map((l) => ({
        item: toCartItem(
          { variantId: l.variantId, parentVariantId: l.parentVariantId, quantity: l.addQty, price: l.currentPrice, name: l.name, skuCode: l.skuCode, image: l.image, inventories: l.inventories },
          lineSupplier,
          l.product,
        ),
        max: l.available,
      })),
    )

  // The row's cart button: a past order goes through «Дахин захиалах»; a saved list
  // is added at catalogue prices (the catalogue re-prices cart lines anyway).
  const handleAddRow = async (id: string) => {
    if (tab === "order") {
      await reorderById(id)
      return
    }
    // Warehouses come from the catalogue; the button stays off until it has loaded.
    if (!catalogue) return
    try {
      const list = await SavedListApi.get(id)
      const products = flattenCatalogue(catalogue)
      const found = (list.lines || []).map((l) => ({ l, product: products.find((p) => p.id === l.variantId) }))
      const entries = found
        .filter(({ product }) => !!product)
        .map(({ l, product }) => ({ item: toCartItem({ variantId: l.variantId, parentVariantId: l.parentVariantId, quantity: l.quantity }, supplier, product) }))
      if (entries.length === 0) {
        message.error(t("bulkOrder:saved.nothingToAdd"))
        return
      }
      // Variants gone from the catalogue have no warehouse and no SKU to show.
      const missing = found.filter(({ product }) => !product).map(() => "")
      addLines(entries, missing)
    } catch (err) {
      message.error(translateBackendError(err))
    }
  }

  const handleDelete = (id: string) => {
    const row = rows.find((r) => r.id === id)
    Dialog.confirm(t("bulkOrder:saved.deleteConfirm", { name: row?.name || "" }), async (key) => {
      if (key !== "confirm") return
      try {
        await SavedListApi.remove(id)
        message.success(t("bulkOrder:saved.deleted"))
        if (selectedId === id) setSelectedId(null)
        saved.mutate()
      } catch (err) {
        message.error(translateBackendError(err))
      }
    })
  }

  const breadcrumb = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/buyer-sales", label: t("order:buyerSales.breadcrumb") },
    { to: "/buyer-sales/new", label: t("order:catalog.title") },
    { label: t("bulkOrder:saved.title") },
  ]

  if (!ORD_LIST.hasAccess) {
    return (
      <PageLayout title={t("bulkOrder:saved.title")} breadcrumb={breadcrumb} noFrame>
        <DeniedCard permission="ORD_LIST" />
      </PageLayout>
    )
  }

  if (suppliersLoading || suppliers.length === 0) {
    return (
      <PageLayout title={t("bulkOrder:saved.title")} breadcrumb={breadcrumb} noFrame>
        <TableFrame>
          {suppliersLoading ? (
            <ListState state="loading" />
          ) : (
            <ListState
              state="empty"
              title={t("supplierSales:customer.noSupplier")}
              description={t("supplierSales:customer.noSupplierDescription")}
              action={{ label: t("supplierSales:customer.backToList"), onClick: () => navigate("/buyer-sales") }}
            />
          )}
        </TableFrame>
      </PageLayout>
    )
  }

  const title = (
    <Box component="span" className={qoClasses.titleRow}>
      <Box component="span">{t("bulkOrder:saved.title")}</Box>
      {suppliers.length > 1 && (
        <SupplierSelect
          suppliers={suppliers}
          value={supplierId}
          onChange={(id) => {
            setSupplierId(id)
            setSelectedId(null)
          }}
        />
      )}
    </Box>
  )

  const extra = [
    <Button key="save" variant="default" size="md" disabled={selectedLines.length === 0 || !lineSupplier?.id} onClick={openSave}>
      {t("bulkOrder:saved.saveAsList")}
    </Button>,
    // One list-header concept: segmented tabs · «?» (@dehub/help types).
    <Group key="tab" gap="xs" wrap="nowrap">
      <SegmentedTabs
        value={tab}
        onChange={(v) => handleTab(v as Tab)}
        data={[
          { value: "saved", label: t("bulkOrder:saved.tabSaved") },
          { value: "order", label: t("bulkOrder:saved.tabOrders") },
        ]}
      />
      <HelpIcon helpKey="order.buyer-sales-saved-lists.types.tabs" />
    </Group>,
  ]

  return (
    <PageLayout title={title} breadcrumb={breadcrumb} extra={extra} noFrame>
      <Box className={classes.layout}>
        <SourceList
          kind={tab}
          rows={rows}
          state={listState}
          selectedId={current?.id || null}
          note={tab === "order" ? t("bulkOrder:saved.ordersNote", { count: orders.data?.count || rows.length }) : undefined}
          onRetry={() => active.mutate()}
          onSelect={setSelectedId}
          onAddToCart={handleAddRow}
          addDisabled={tab === "saved" && !catalogue}
          onDelete={tab === "saved" ? handleDelete : undefined}
          onEmptyAction={() => navigate("/buyer-sales/new")}
        />
        {source && (
          <ReorderPanel
            title={source.name}
            date={source.date}
            lines={lines}
            isLoading={linesLoading}
            error={linesError}
            isSelected={isSelected}
            onToggle={handleToggle}
            onRetry={retry}
            onAdd={handleAddPanel}
          />
        )}
      </Box>
      <SaveListModal
        opened={saveOpened}
        onClose={closeSave}
        groups={
          lineSupplier?.id
            ? [
                {
                  supplierBusinessId: lineSupplier.id,
                  supplierName: lineSupplier.name || "",
                  lines: selectedLines.map((l) => ({ variantId: l.variantId, parentVariantId: l.parentVariantId, quantity: l.quantity })),
                },
              ]
            : []
        }
        onSaved={() => {
          if (tab === "saved") saved.mutate()
        }}
      />
    </PageLayout>
  )
}
